import { Routes, Route, Navigate } from 'react-router-dom'
import { useAuthStore } from '@stores/auth'
import { useAuthValidation } from '@hooks/useAuthValidation'
import { LoginPage } from '@pages/LoginPage'
import { DashboardPage } from '@pages/DashboardPage'
import { CampaignsPage } from '@pages/CampaignsPage'
import { ClientsPage } from '@pages/ClientsPage'
import { TemplatesPage } from '@pages/TemplatesPage'
import { MailboxesPage } from '@pages/MailboxesPage'
import { InstantSendPage } from '@pages/InstantSendPage'
import { WhatsAppMailboxesPage } from '@pages/WhatsAppMailboxesPage'
import { QueuePage } from '@pages/QueuePage'
import { SettingsPage } from '@pages/SettingsPage'
import { InboxPage } from '@pages/InboxPage'
import { TrackingPage } from '@pages/TrackingPage'
import { AppLayout } from '@components/layout/AppLayout'
import { CampaignDetail } from '@components/campaigns/CampaignDetail'
import WhatsAppTest from '@pages/WhatsAppTest'
import {
  WaContactsPage,
  WaCampaignsPage,
  WaCampaignDetail,
  WaTemplatesPage,
  WaInboxPage,
  WaSettingsPage,
  WaMailboxesPage,
} from '@pages/whatsapp'

interface ProtectedRouteProps {
  children: React.ReactNode
}

function ProtectedRoute({ children }: ProtectedRouteProps) {
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated)

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />
  }

  return <>{children}</>
}

export function AppRoutes() {
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated)
  const { isValidating } = useAuthValidation()

  // Show loading while validating session
  if (isValidating) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-violet-500"></div>
      </div>
    )
  }

  return (
    <Routes>
      <Route
        path="/login"
        element={isAuthenticated ? <Navigate to="/dashboard" replace /> : <LoginPage />}
      />

      <Route
        path="/"
        element={
          <ProtectedRoute>
            <AppLayout />
          </ProtectedRoute>
        }
      >
        <Route index element={<Navigate to="/dashboard" replace />} />
        <Route path="dashboard" element={<DashboardPage />} />
        <Route path="campaigns" element={<CampaignsPage />} />
        <Route path="campaigns/:id" element={<CampaignDetail />} />
        <Route path="instant-send" element={<InstantSendPage />} />
        <Route path="inbox" element={<InboxPage />} />
        <Route path="tracking" element={<TrackingPage />} />
        <Route path="clients" element={<ClientsPage />} />
        <Route path="templates" element={<TemplatesPage />} />
        <Route path="mailboxes" element={<MailboxesPage />} />
        <Route path="whatsapp-mailboxes" element={<WhatsAppMailboxesPage />} />
        <Route path="queue" element={<QueuePage />} />
        <Route path="settings" element={<SettingsPage />} />
        <Route path="whatsapp-test" element={<WhatsAppTest />} />

        {/* WhatsApp Module Routes */}
        <Route path="whatsapp">
          <Route index element={<Navigate to="/whatsapp/inbox" replace />} />
          <Route path="inbox" element={<WaInboxPage />} />
          <Route path="contacts" element={<WaContactsPage />} />
          <Route path="campaigns" element={<WaCampaignsPage />} />
          <Route path="campaigns/:id" element={<WaCampaignDetail />} />
          <Route path="templates" element={<WaTemplatesPage />} />
          <Route path="mailboxes" element={<WaMailboxesPage />} />
          <Route path="settings" element={<WaSettingsPage />} />
        </Route>
      </Route>

      <Route path="*" element={<Navigate to="/dashboard" replace />} />
    </Routes>
  )
}
